import React from "react";
import { Link } from "react-router-dom";
import { money } from "../../lib/api";
import { useCart } from "../../lib/cart";
import { imgSrcSet } from "../../lib/imgSrcSet";
import SafeImage from "./SafeImage";
import FitmentBadge from "./FitmentBadge";

export default function ProductCard({ product, compact = false }) {
  const { add } = useCart();
  const img = product.images?.[0];
  const imgProps = imgSrcSet(img, { sizes: "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" });
  const inStock = product.inventory_qty > 0;

  return (
    <div
      data-testid={`product-card-${product.sku}`}
      className="group relative bg-[#0d0d0d] border border-white/5 hover:border-white/15 transition-all duration-300 rounded-md overflow-hidden flex flex-col"
    >
      <Link to={`/product/${product.slug}`} className="block relative aspect-[4/3] overflow-hidden bg-white border-b border-zinc-200/70">
        <SafeImage
          {...imgProps}
          alt={`${product.name} — ${product.category === "side-mirror" ? "Side Mirror" : "Windshield"}${product.position && product.position !== "n/a" ? " (" + product.position + ")" : ""} for sale at Lecis Auto Glass`}
          className="absolute inset-0 w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-700"
          loading="lazy"
          decoding="async"
          fallbackLabel={product.name}
        />
        <div className="absolute top-3 left-3 flex gap-2">
          {product.fitment_type === "oem-style" && (
            <span className="text-[9px] uppercase tracking-[0.2em] font-bold bg-white/90 text-black px-2 py-1">OEM-Style</span>
          )}
          {product.position === "left" && <span className="text-[9px] uppercase tracking-[0.2em] font-bold bg-red-600/90 text-white px-2 py-1">Left</span>}
          {product.position === "right" && <span className="text-[9px] uppercase tracking-[0.2em] font-bold bg-red-600/90 text-white px-2 py-1">Right</span>}
          {product.position === "pair" && <span className="text-[9px] uppercase tracking-[0.2em] font-bold bg-red-600/90 text-white px-2 py-1">Pair</span>}
        </div>
        {!inStock && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="text-xs uppercase tracking-[0.25em] text-white">Out of stock</span>
          </div>
        )}
      </Link>

      <div className="p-4 flex-1 flex flex-col">
        <Link to={`/product/${product.slug}`} className="text-sm md:text-base font-medium leading-snug mb-3 hover:text-red-500 transition-colors">
          {product.name}
        </Link>

        {!compact && product.fit_confidence && product.queried_fit && (
          <div
            className="mb-3"
            data-testid={product.fit_confidence === "verified" ? "card-verified-fit" : `card-fit-${product.fit_confidence}`}
          >
            <FitmentBadge
              confidence={product.fit_confidence}
              label={`${product.queried_fit.year} ${(product.queried_fit.make_slug || "").toUpperCase()} ${(product.queried_fit.model_slug || "").toUpperCase()}`}
            />
          </div>
        )}
        {!compact && !product.fit_confidence && product.fitments?.length > 0 && (
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">
            Fits {product.fitments.slice(0, 2).map((f) => `${f.year} ${f.make} ${f.model}`).join(", ")}
            {product.fitments.length > 2 && ` +${product.fitments.length - 2}`}
          </div>
        )}

        <div className="mt-auto flex items-end justify-between">
          <div className="font-mono text-lg text-white">{money(product.price)}</div>
          <button
            data-testid={`product-add-${product.sku}`}
            disabled={!inStock}
            onClick={() => add(product)}
            className="text-xs uppercase tracking-[0.2em] font-bold border border-white/20 hover:border-red-500 hover:text-red-500 px-4 py-2 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {inStock ? "Add" : "—"}
          </button>
        </div>
      </div>
    </div>
  );
}
